// src/components/Label/Label.tsx
import React, { useRef, useState, useEffect } from 'react';
import { Box, SxProps } from '@mui/material';

const CONNECTOR_DOT_SIZE = 3;

export interface Props {
  labelHeight?: number;
  maxWidth: number;
  maxHeight?: number;
  expandDirection?: 'CENTER' | 'BOTTOM';
  children: React.ReactNode;
  sx?: SxProps;
}

export const Label = ({
  children,
  maxWidth,
  maxHeight,
  expandDirection = 'CENTER',
  labelHeight = 0,
  sx
}: Props) => {
  const contentRef = useRef<HTMLDivElement>();
  const [isDark, setIsDark] = useState(false);

  // Watch for dark mode changes by observing the 'dark' class on document.documentElement
  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Check initial theme
    checkTheme();

    // Watch for class changes on document element
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        width: maxWidth
      }}
    >
      {labelHeight > 0 && (
        <Box
          component="svg"
          viewBox={`0 0 ${CONNECTOR_DOT_SIZE} ${labelHeight}`}
          width={CONNECTOR_DOT_SIZE}
          sx={{
            position: 'absolute',
            top: -labelHeight,
            left: -CONNECTOR_DOT_SIZE / 2
          }}
        >
          <line
            x1={CONNECTOR_DOT_SIZE / 2}
            y1={0}
            x2={CONNECTOR_DOT_SIZE / 2}
            y2={labelHeight}
            strokeDasharray={`0, ${CONNECTOR_DOT_SIZE * 2}`}
            // FIX: Connector line adapts to dark mode
            stroke={isDark ? '#ffffff' : '#000000'}
            strokeWidth={CONNECTOR_DOT_SIZE}
            strokeLinecap="round"
          />
        </Box>
      )}

      <Box
        ref={contentRef}
        // CRITICAL FIX: Add data attribute for export CSS targeting
        data-flowi-label="true"
        className="flowi-label"
        sx={{
          position: 'absolute',
          display: 'inline-block',
          boxSizing: 'border-box',
          // Enhanced background with better styling
          // Dark mode: fully opaque single color, no blur, minimal shadow for flat appearance
          bgcolor: isDark ? 'rgba(30, 41, 59, 1)' : 'rgba(255, 255, 255, 0.98)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(71, 85, 105, 1)' : 'rgba(226, 232, 240, 0.8)',
          color: isDark ? 'common.white' : 'text.primary',
          borderRadius: '10px',
          py: 0.875,
          px: 1.5,
          transformOrigin: 'bottom center',
          transform: `translate(-50%, ${
            expandDirection === 'BOTTOM' ? '-100%' : '-50%'
          })`,
          overflow: 'hidden',
          // Enhanced backdrop blur and shadow (blur disabled in dark mode to prevent gradient effect)
          backdropFilter: isDark ? 'none' : 'blur(12px)',
          boxShadow: isDark
            ? '0 2px 4px rgba(0, 0, 0, 0.3)'
            : '0 3px 12px rgba(0, 0, 0, 0.07), 0 1px 5px rgba(0, 0, 0, 0.04)',
          // Smooth transitions
          transition:
            'background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease',
          // Hover effect - subtle lift
          '&:hover': {
            boxShadow: isDark
              ? '0 3px 6px rgba(0, 0, 0, 0.4)'
              : '0 6px 20px rgba(0, 0, 0, 0.1), 0 3px 10px rgba(0, 0, 0, 0.06)',
            borderColor: isDark ? 'rgba(96, 165, 250, 0.3)' : 'rgba(59, 130, 246, 0.2)',
          },
          ...sx
        }}
        style={{
          maxHeight,
          top: -labelHeight
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
